import { isTauriRuntime } from "src/lib/runtime/environment";

export type DeepLinkUnlisten = () => void;
export type OAuthDeepLinkProvider = "gmail" | "quickbooks";
export type OAuthDeepLinkResult = {
  provider: OAuthDeepLinkProvider;
  status: "success" | "error";
  connectionId?: string;
  error?: string;
};

/**
 * "launch" is the plugin's remembered latest link, replayed on subscribe: it is still there after a
 * webview reload, so a handler that navigates must not act on the same launch link twice.
 */
export type DeepLinkSource = "launch" | "open";

export const onDeepLink = async (
  handler: (urls: string[], source: DeepLinkSource) => void
): Promise<DeepLinkUnlisten> => {
  if (!isTauriRuntime()) {
    return () => {};
  }

  const { getCurrent, onOpenUrl } = await import("@tauri-apps/plugin-deep-link");
  const current = await getCurrent();
  if (current?.length) {
    handler(current, "launch");
  }

  return onOpenUrl((urls) => handler(urls, "open"));
};

export const parseOAuthDeepLink = (url: string): OAuthDeepLinkResult | undefined => {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return undefined;
  }

  if (parsed.protocol !== "fraction-employee:") {
    return undefined;
  }

  const provider = getOAuthProvider(parsed);
  if (!provider) {
    return undefined;
  }

  const rawStatus = parsed.searchParams.get("status");
  const status = rawStatus === "success" ? "success" : "error";
  return {
    provider,
    status,
    connectionId: parsed.searchParams.get("connectionId") ?? undefined,
    error: parsed.searchParams.get("error") ?? undefined,
  };
};

export const waitForOAuthDeepLink = async (
  provider: OAuthDeepLinkProvider,
  timeoutMs = 2 * 60 * 1000
): Promise<OAuthDeepLinkResult | undefined> => {
  if (!isTauriRuntime()) {
    return undefined;
  }

  return new Promise((resolve) => {
    let resolved = false;
    let unlisten: DeepLinkUnlisten | undefined;

    const finish = (result?: OAuthDeepLinkResult) => {
      if (resolved) {
        return;
      }
      resolved = true;
      clearTimeout(timeout);
      unlisten?.();
      resolve(result);
    };

    const timeout = setTimeout(() => finish(), timeoutMs);

    void onDeepLink((urls) => {
      for (const url of urls) {
        const result = parseOAuthDeepLink(url);
        if (result?.provider === provider) {
          finish(result);
          return;
        }
      }
    }).then((nextUnlisten) => {
      unlisten = nextUnlisten;
      if (resolved) {
        unlisten();
      }
    });
  });
};

const getOAuthProvider = (url: URL): OAuthDeepLinkProvider | undefined => {
  const host = url.hostname;
  const path = url.pathname.replace(/^\/+/, "");

  if (host === "oauth" && path === "gmail") {
    return "gmail";
  }
  if (host === "oauth" && path === "quickbooks") {
    return "quickbooks";
  }
  if (host === "gmail" || path === "gmail") {
    return "gmail";
  }
  if (host === "quickbooks" || path === "quickbooks") {
    return "quickbooks";
  }

  return undefined;
};
